import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createUnitRequest,
  deleteUnitRequest,
  getUnitsRequest,
  updateUnitRequest,
} from "../../api/unitApi";
import { CreateUnitRequest } from "../../models/RequestModels/CreateUnitRequest";
import { UnitResponse } from "../../models/ResponseModels/UnitResponse";
import {
  getApiErrorMessage,
  getApiErrorPayload,
} from "../../utils/helperFunctions/apiErrorHelper";

interface UnitState {
  units: UnitResponse[];
  loading: boolean;
  error: string | null;
}

const initialState: UnitState = {
  units: [],
  loading: false,
  error: null,
};

export const fetchUnits = createAsyncThunk(
  "units/fetchUnits",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getUnitsRequest();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const createUnit = createAsyncThunk(
  "units/createUnit",
  async (body: CreateUnitRequest, { rejectWithValue }) => {
    try {
      const response = await createUnitRequest(body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const updateUnit = createAsyncThunk(
  "units/updateUnit",
  async (
    payload: { id: number; body: Partial<CreateUnitRequest> },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateUnitRequest(payload.id, payload.body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const deleteUnit = createAsyncThunk(
  "units/deleteUnit",
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteUnitRequest(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

const unitSlice = createSlice({
  name: "units",
  initialState,
  reducers: {
    clearUnitError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUnits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnits.fulfilled, (state, action) => {
        state.loading = false;
        state.units = action.payload;
      })
      .addCase(fetchUnits.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to load units.",
        );
      })

      .addCase(createUnit.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUnit.fulfilled, (state, action) => {
        state.loading = false;
        state.units.unshift(action.payload);
      })
      .addCase(createUnit.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to create unit.",
        );
      })

      .addCase(updateUnit.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUnit.fulfilled, (state, action) => {
        state.loading = false;
        state.units = state.units.map((unit) =>
          unit.id === action.payload.id ? action.payload : unit,
        );
      })
      .addCase(updateUnit.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to update unit.",
        );
      })

      .addCase(deleteUnit.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUnit.fulfilled, (state, action) => {
        state.loading = false;
        state.units = state.units.filter((unit) => unit.id !== action.payload);
      })
      .addCase(deleteUnit.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to delete unit.",
        );
      });
  },
});

export const { clearUnitError } = unitSlice.actions;

export default unitSlice.reducer;
